import { CLIENTS_ENDPOINT } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import { Client } from "../../../../types/clients";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { AddressDisplay } from "../../../../components/ui/addressDisplay/addressDisplay.component";
import { Box, Typography, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { basicInfoContainerStyle } from "../../../../styles/commonStyles";
import { useHasPermission } from "../../../../contexts/auth";
import { formatDateTime } from "../../../../utils";

type ClientBasicInfoProps = {
  previewId?: string | number;
};

type DetailRowProps = {
  label: string;
  children: ReactNode;
  noBorder?: boolean;
};

const DetailRow = ({ label, children, noBorder = false }: DetailRowProps) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" },
      gap: { xs: 0.5, sm: 2 },
      alignItems: "start",
      py: 1.25,
      px: 1.5,
      borderBottom: noBorder ? "none" : "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ minHeight: "24px", display: "flex", alignItems: "center" }}>{children}</Box>
  </Box>
);

const formatTime = (time?: string) => {
  if (!time) {
    return "";
  }
  // Remove seconds from time string (e.g., "08:00:00" -> "08:00")
  return time.split(":").slice(0, 2).join(":");
};

export const ClientBasicInfo = ({ previewId }: ClientBasicInfoProps) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(CLIENTS_ENDPOINT);
  const [client, setClient] = useState<Client | null>(null);
  const canViewClients = useHasPermission("settlement.view_client");
  const entityId = previewId ?? id;

  useEffect(() => {
    const fetchClient = async () => {
      if (entityId) {
        const response = await get(entityId);
        setClient(response.data);
      }
    };

    const handleClientUpdated = () => {
      fetchClient();
    };

    window.addEventListener("clientUpdated", handleClientUpdated);
    fetchClient();
    return () => {
      window.removeEventListener("clientUpdated", handleClientUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  if (!canViewClients) {
    return null;
  }

  if (!client) return <CustomCircularProgress />;

  return (
    <Box
      sx={basicInfoContainerStyle}
    >
      {/* Header Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {client.short_name || client.name}
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Basic Information Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Basic Information")}
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <DetailRow label={t("Name")}>
            <Typography variant="subtitle1">{client.name || "-"}</Typography>
          </DetailRow>
          {client.short_name != null && client.short_name !== "" && (
            <DetailRow label={t("Short name")} noBorder>
              <Typography variant="subtitle1">{client.short_name}</Typography>
            </DetailRow>
          )}
        </Box>
      </Box>

      <Divider />

      {/* Identification Numbers Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Identification Numbers")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="subtitle1">
            <strong>{t("PESEL")}:</strong> {client.pesel || "-"}
          </Typography>
          <Typography variant="subtitle1">
            <strong>{t("NIP")}:</strong> {client.nip || "-"}
          </Typography>
          <Typography variant="subtitle1">
            <strong>{t("REGON")}:</strong> {client.regon || "-"}
          </Typography>
          <Typography variant="subtitle1">
            <strong>{t("KRS")}:</strong> {client.krs || "-"}
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Address Information Section */}
      <AddressDisplay address={client.address} title={t("Address Information")} />

      <Divider />

      {/* Contact Information Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Contact Information")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="subtitle1">
            <strong>{t("Phone Number")}:</strong>{" "}
            {client.address.phone_number || "-"}
          </Typography>
          <Typography variant="subtitle1">
            <strong>{t("Email")}:</strong> {client.address.email || "-"}
          </Typography>
          {client.address.email_invoices && (
            <Typography variant="subtitle1">
              <strong>{t("Invoices Email")}:</strong>{" "}
              {client.address.email_invoices}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Additional Information Section */}
      {(client.address.notes || client.address.opening_hours !== undefined) && (
        <>
          <Divider />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t("Additional Information")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {client.address.notes && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    <strong>{t("Notes")}:</strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      color: "text.secondary",
                      pl: 2,
                    }}
                  >
                    {client.address.notes}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  <strong>{t("Opening Hours")}:</strong>
                </Typography>
                <Box sx={{ pl: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {Array.isArray(client.address.opening_hours) &&
                  client.address.opening_hours.length > 0 ? (
                    client.address.opening_hours.map((oh: any, idx: number) => {
                      const dayNames: { [key: string]: string } = {
                        "1": t("Monday"),
                        "2": t("Tuesday"),
                        "3": t("Wednesday"),
                        "4": t("Thursday"),
                        "5": t("Friday"),
                        "6": t("Saturday"),
                        "7": t("Sunday"),
                      };
                      return (
                        <Typography
                          key={oh.id || idx}
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          {typeof oh === "string"
                            ? oh
                            : `${dayNames[oh.week_day] || oh.week_day}: ${formatTime(oh.from_hour)} - ${formatTime(oh.to_hour)}`}
                        </Typography>
                      );
                    })
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", fontStyle: "italic" }}
                    >
                      {t("No opening hours")}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}

      <Divider />

      {/* Metadata Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Metadata")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Created At")}:</strong> {formatDateTime(client.created_at)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Updated At")}:</strong> {formatDateTime(client.updated_at)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
